import {
  CONVERGE_UUID,
  EndorsementUuid,
  SubCoverageUuid,
  TEMP_ENDORSEMENT_SIMILAR_PREMIUM_BEARING,
  isConverge,
} from '@converge/data';
import {
  CoverageErrorMessageDetailsArray,
  IRaterContextState,
  IRaterProps,
  IUnderwritingSocketQuoteInput,
  RaterColumnCount,
  RaterTab,
  UnderwritingCoverageReceivedMessage,
} from '../types';
import {
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  IUnderwritingEnterpriseRating,
  IUnderwritingQuote,
  IUnderwritingQuoteExcessLayerParticipant,
  IUnderwritingQuoteExcessTowerLayer,
  IUnderwritingScheduleRating,
  IUnderwritingSubCoverage,
  IUnderwritingSubjectivity,
  Provider,
  RaterParticipantCoverageDetails,
  RaterParticipantSubCoverageDetails,
  RaterPolicyDetails,
  RaterUserQuotes,
  SaveAnswerInput,
  SignificantTermsConditions,
  Submission,
  addMetadataToCoverage,
  addMetadataToEndorsements,
  getDisplayNameFromEntity,
} from '@converge/graphql';
import {
  ImmerReducer,
  createActionCreators,
  createReducerFunction,
} from 'immer-reducer';
import config, { convergeLogger } from '@converge/config';
import { formatList, handleError, isNotNullOrUndefined } from '@converge/utils';

import { ApolloError } from '@apollo/client/errors';
import { Draft } from 'immer';
import { createRaterData } from './rater.data';
import { createRaterRequestMessage } from './rater.message.create';
import { getLayerLimitFromParticipants } from './utils';
import moment from 'moment';
import pluralize from 'pluralize';
import { receiveRaterMessageAndUpdateState } from './rater.message.receive';
import { useReducer } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type WritableDraft<T> = { -readonly [K in keyof T]: Draft<T[K]> };

export class RaterReducer extends ImmerReducer<IRaterContextState> {
  get logger() {
    return {
      log: (...args: unknown[]) => convergeLogger.log('RaterReducer', ...args),
      warn: (...args: unknown[]) =>
        convergeLogger.warn('RaterReducer', ...args),
      debug: (...args: unknown[]) =>
        convergeLogger.debug('RaterReducer', ...args),
      error: (...args: unknown[]) =>
        convergeLogger.error('RaterReducer', ...args),
    };
  }

  constructor(
    draftState: Draft<IRaterContextState>,
    state: IRaterContextState
  ) {
    super(draftState, state);

    // TODO: This shouldn't be needed:
    this.draftState.isEditable =
      draftState.isEditable ?? state.options.isEditable;

    this.draftState.quote.coverages = this.draftState.quote.coverages.map(
      addMetadataToCoverage
    );

    this.draftState.quote.endorsements = this.draftState.quote.endorsements.map(
      addMetadataToEndorsements
    ) as WritableDraft<IUnderwritingEndorsement>[];

    const invalidScheduleAndRatings =
      this.draftState.quote.scheduleRatings.filter(
        (rating) => !rating.primary && !rating.excess
      );

    if (invalidScheduleAndRatings.length > 0) {
      handleError({
        source: 'constructor',
        message: `Found ${invalidScheduleAndRatings.length} schedule rating(s) that are neither primary nor excess.`,
      });
    }

    this.draftState.quote.excessTowerLayers.map((layer) => {
      return {
        ...layer,
        participants: sortExcessTowerLayerParticipants(layer.participants),
      };
    });

    this.updateCalculatedQuoteData('constructor');
  }

  setEndoresementAnswers(answers: Record<string, SaveAnswerInput[]>) {
    this.draftState.endoresementAnswers = answers;
  }

  setUpsertAnswersState({
    loading,
    error,
  }: {
    loading: boolean;
    error?: ApolloError;
  }) {
    this.logger.log(
      'setUpsertAnswersState',
      `loading: ${loading}`,
      `error: ${error}`
    );

    this.draftState.upsertAnswersIsLoading = loading;
    this.draftState.upsertAnswersError = error ? error.message : undefined;
  }

  setIsInvalidRaterData(isInvalidData: boolean) {
    this.draftState.isInvalidData = isInvalidData;
  }

  setTrackingConflict(hasTrackingConflict: boolean) {
    this.draftState.hasTrackingConflict = hasTrackingConflict;
  }

  setUpsertQuoteState({
    loading,
    error,
  }: {
    loading: boolean;
    error?: ApolloError;
  }) {
    this.logger.log(
      'setUpsertQuoteState',
      `loading: ${loading}`,
      `error: ${error}`
    );

    if (loading) {
      this.fulfillRequestForUpsertQuote();
    }

    this.draftState.upsertQuoteIsLoading = loading;
    this.draftState.upsertQuoteError = error ? error.message : undefined;
  }

  fulfillRequestForUpsertQuote() {
    this.logger.log('fulfillRequestForUpsertQuote');

    this.draftState.isRequestingUpsertQuoteNow = false;
    this.draftState.isRequestingUpsertQuoteAfterRating = false;
  }

  requestUpsertQuote(source: string) {
    this.logger.log('requestUpsertQuote', source);

    this.draftState.isRequestingUpsertQuoteAfterRating = false;
    this.draftState.isRequestingUpsertQuoteNow = true;
  }

  requestRatingUpdateAndUpsertQuote(source: string) {
    this.logger.log('requestRatingUpdateAndUpsertQuote', source);

    this.draftState.isRequestingUpsertQuoteAfterRating = true;
    this.requestRatingUpdate(`requestRatingUpdateAndUpsertQuote:${source}`);
  }

  requestRatingUpdate(source: string) {
    // Record which tab this edit originated on, so the Excess "save first" lock
    // doesn't fire for edits made inside the Excess tab itself (those re-rate and
    // mark top-level coverages hasChanged as a side effect).
    this.draftState.lastEditedTab = this.draftState.tab;

    this.updateCalculatedQuoteData(`requestRatingUpdate:${source}`);

    const reasonsWhyRatingRequestShouldNotHaveBeenSent: string[] = [];

    if (!this.draftState.isEditable) {
      reasonsWhyRatingRequestShouldNotHaveBeenSent.push(
        'quote is not editable'
      );
    }

    if (!this.draftState.isAuthenticated) {
      reasonsWhyRatingRequestShouldNotHaveBeenSent.push(
        'rater connection is not yet authenticated'
      );
    }

    if (this.draftState.isRequestingUpsertQuoteNow) {
      reasonsWhyRatingRequestShouldNotHaveBeenSent.push(
        'quote has been requested to be saved'
      );
    }

    if (this.draftState.upsertQuoteIsLoading) {
      convergeLogger.warn(
        'Rating request should not have been sent because quote is being saved'
      );
    }

    if (reasonsWhyRatingRequestShouldNotHaveBeenSent.length > 0) {
      handleError({
        source: `requestRatingUpdate:${source}`,
        message: `Rating request should not have been sent because: ${formatList(
          reasonsWhyRatingRequestShouldNotHaveBeenSent
        )}`,
      });
    }

    try {
      const message: IUnderwritingSocketQuoteInput = createRaterRequestMessage(
        this.draftState
      );
      this.logger.log('requestRatingUpdate', message);

      this.draftState.isRequestingRatingUpdate = true;
      this.sendSocketMessage(message);
    } catch (cause) {
      handleError({
        source: 'requestRatingUpdate',
        cause,
        message: `Frontend failed to prepare rater request: ${cause}`,
      });
    }
  }

  setScheduleRatingPercentage(uuid: string, value: number) {
    this.logger.log('setScheduleRatingPercentage', uuid, value);

    this.draftState.quote = {
      ...this.draftState.quote,
      scheduleRatings: this.draftState.quote.scheduleRatings.map(
        (rating): IUnderwritingScheduleRating => {
          if (rating.uuid === uuid) {
            return {
              ...rating,
              hasChanged: value !== rating.quote.percentage,
              quote: {
                ...rating.quote,
                percentage: value ?? 0,
              },
            };
          }
          return rating;
        }
      ),
    };

    this.markQuoteAsChanged('setScheduleRatingPercentage');

    this.requestRatingUpdate('setScheduleRatingPercentage');
  }

  markQuoteAsChanged(reason: string) {
    this.logger.log('markQuoteAsChanged', reason);

    this.draftState.quote.changeReason = reason;
    this.draftState.quote.hasChanged = true;
  }

  removeExcessLayer(excessLayerUuid: string) {
    this.logger.log('removeExcessLayer', excessLayerUuid);

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers
        .filter((layer) => layer.uuid !== excessLayerUuid)
        .map((layer, index) => ({
          ...layer,
          position: index,
          name: index === 0 ? 'Primary Layer' : `Excess Layer ${index}`,
        }));

    this.markQuoteAsChanged('removeExcessLayer');

    this.requestRatingUpdate('removeExcessLayer');
  }

  cascadeCyberCrimeLimitAndRetention(
    limit: number | null,
    retention: number | null
  ) {
    this.clearRaterError();
    this.setIsInvalidRaterData(false);
    const cyberCrimeCoverage = this.draftState.quote.coverages.find(
      (item) => item.name === 'Cyber Crime'
    );

    if (cyberCrimeCoverage) {
      const updatedCyberCrimeCoverage = {
        ...cyberCrimeCoverage,
        hasChanged: true,
        quote: {
          ...cyberCrimeCoverage.quote,
          limit: limit,
          retention: retention,
        },

        subCoverages: cyberCrimeCoverage.subCoverages.map((subCoverage) => ({
          ...subCoverage,
          hasChanged: true,
          quote: {
            ...subCoverage.quote,
            subLimit: limit,
            subRetention: retention,
          },
        })),
      };

      const updatedCoverages = this.draftState.quote.coverages.map((coverage) =>
        coverage.name === 'Cyber Crime'
          ? updatedCyberCrimeCoverage
          : {
              ...coverage,
              hasChanged: true,
              quote: {
                ...coverage.quote,
                retention: retention,
              },
              subCoverages: coverage.subCoverages.map((subCoverage) => ({
                ...subCoverage,
                hasChanged: true,
                quote: {
                  ...subCoverage.quote,

                  subRetention: retention,
                },
              })),
            }
      );

      this.draftState.quote.coverages = updatedCoverages;

      this.draftState.quote.endorsements =
        this.draftState.quote.endorsements.map((endorsement) => {
          if (endorsement.nonPremiumBearing) {
            return endorsement;
          }
          // Allow retention to cascade for all endorsements
          return {
            ...endorsement,
            hasChanged: true,
            quote: {
              ...endorsement.quote,
              retention: retention,
            },
          };
        });
    }
    this.markQuoteAsChanged('cascadeLimitAndRetention');

    this.requestRatingUpdate('cascadeLimitAndRetention');
  }

  cascadeLimitAndRetention(limit: number | null, retention: number | null) {
    this.clearRaterError();
    this.setIsInvalidRaterData(false);
    const minLimit = 1000000;
    this.draftState.quote.coverages = this.draftState.quote.coverages.map(
      (coverage) => {
        const isSecurityCoverage = coverage.name === 'Cyber Crime';
        return {
          ...coverage,
          hasChanged: true,
          quote: {
            ...coverage.quote,
            limit: isSecurityCoverage ? coverage.quote.limit : limit,
            retention: retention,
          },
          subCoverages: coverage.subCoverages.map((subCoverage) => ({
            ...subCoverage,
            hasChanged: true,
            quote: {
              ...subCoverage.quote,

              subRetention: retention,
            },
          })),
        };
      }
    );

    this.draftState.quote.endorsements = this.draftState.quote.endorsements.map(
      (endorsement) => {
        if (endorsement.nonPremiumBearing) {
          return {
            ...endorsement,
            hasChanged: true,
            quote: { ...endorsement.quote, limit, retention },
          };
        }
        // Tracking Technology: cascade limit normally, but retention is min 25k
        if (
          endorsement.uuid ===
          EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
        ) {
          return {
            ...endorsement,
            hasChanged: true,
            quote: {
              ...endorsement.quote,
              limit,
              retention: Math.max(25000, retention ?? 0),
            },
          };
        }
        // Exclude specific endorsements from limit cascading, but allow retention cascading
        if (
          endorsement.uuid === EndorsementUuid.MissedBidAmendatory ||
          endorsement.uuid ===
            EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit ||
          endorsement.uuid === EndorsementUuid.CyberEventAmendatory
        ) {
          return {
            ...endorsement,
            hasChanged: true,
            quote: {
              ...endorsement.quote,
              // Keep the original limit, but cascade the retention
              retention,
            },
          };
        }
        return {
          ...endorsement,
          hasChanged: true,
          quote: {
            ...endorsement.quote,
            limit:
              endorsement.uuid ===
                EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit &&
              limit &&
              limit > minLimit
                ? 1000000
                : endorsement.uuid ===
                  EndorsementUuid.ContingentBodilyInjuryAndPropertyDamageEndorsement
                ? 250000
                : endorsement.uuid ===
                    EndorsementUuid.AdaClaimsExpensesSublimit ||
                  endorsement.uuid ===
                    EndorsementUuid.BipaClaimsExpensesSublimit ||
                  endorsement.uuid ===
                    EndorsementUuid.TcpaClaimsExpensesSublimit
                ? 25000
                : limit,
            retention,
          },
        };
      }
    );

    this.markQuoteAsChanged('cascadeLimitAndRetention');

    this.requestRatingUpdate('cascadeLimitAndRetention');
  }

  cascadeProofOfLossChanges(
    updatedSubCoverage: IUnderwritingCoverage | IUnderwritingSubCoverage
  ) {
    const newSubLimit = updatedSubCoverage.quote?.subLimit;
    const newSubRetention = updatedSubCoverage.quote?.subRetention;

    // Update all other Proof of Loss subcoverages with the same values
    this.draftState.quote.coverages = this.draftState.quote.coverages.map(
      (coverage) => {
        const updatedSubCoverages = coverage.subCoverages.map((subCoverage) => {
          // Only update other Proof of Loss subcoverages (not the one that was just updated)
          if (
            subCoverage.uuid === SubCoverageUuid.ProofOfLoss &&
            subCoverage.quote?.uuid !== updatedSubCoverage.quote?.uuid &&
            // Fallback comparison if quote.uuid is not available
            subCoverage !== updatedSubCoverage
          ) {
            return {
              ...subCoverage,
              hasChanged: true,
              quote: {
                ...subCoverage.quote,
                subLimit: newSubLimit,
                subRetention: newSubRetention,
              },
            };
          }
          return subCoverage;
        });

        const hasCascadedSubCoverage = updatedSubCoverages.some(
          (sub, i) => sub !== coverage.subCoverages[i]
        );

        return {
          ...coverage,
          hasChanged: coverage.hasChanged || hasCascadedSubCoverage,
          subCoverages: updatedSubCoverages,
        };
      }
    );
  }

  //////////////////////////////
  cascadeTowerCyberCrimeLimitAndRetention(
    limit: number | null,
    layerUuid: string,
    participantUuid: string
  ) {
    this.clearRaterError();
    this.setIsInvalidRaterData(false);

    let hasUpdatedLayer = false;
    let hasUpdatedParticipant = false;
    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === layerUuid) {
          if (hasUpdatedLayer) {
            handleError({
              source: 'updateExcessTowerLayerParticipantCoverage',
              message: `Found multiple layers with uuid ${layerUuid}`,
            });
          }
          hasUpdatedLayer = true;

          return {
            ...layer,
            participants: sortExcessTowerLayerParticipants(
              layer.participants.map(
                (participant): IUnderwritingQuoteExcessLayerParticipant => {
                  if (participant.uuid === participantUuid) {
                    if (hasUpdatedParticipant) {
                      handleError({
                        source: 'updateExcessTowerLayerParticipantCoverage',
                        message: `Found multiple participants with uuid ${participantUuid}`,
                      });
                    }
                    hasUpdatedParticipant = true;

                    return {
                      ...participant,
                      coverages: participant.coverages.map(
                        (coverage): RaterParticipantCoverageDetails => {
                          const isSecurityCoverage =
                            coverage.name === 'Cyber Crime';
                          return {
                            ...coverage,
                            hasChanged: true,
                            quote: {
                              ...coverage.quote,
                              limit: isSecurityCoverage
                                ? limit
                                : coverage.quote.limit,
                            },
                            subCoverages: coverage.subCoverages.map(
                              (
                                subCoverage
                              ): RaterParticipantSubCoverageDetails => {
                                return {
                                  ...subCoverage,
                                  hasChanged: true,
                                  quote: {
                                    ...subCoverage.quote,
                                    subLimit: isSecurityCoverage
                                      ? limit
                                      : subCoverage.quote.subLimit,
                                  },
                                };
                              }
                            ),
                          };
                        }
                      ),
                    };
                  }
                  return participant;
                }
              )
            ),
          };
        }

        return layer;
      });

    this.markQuoteAsChanged('cascadeTowerCyberCrimeLimitAndRetention');

    this.requestRatingUpdate('cascadeTowerCyberCrimeLimitAndRetention');
  }

  cascadeTowerLimitAndRetention(
    limit: number | null,
    layerUuid: string,
    participantUuid: string
  ) {
    let hasUpdatedLayer = false;
    let hasUpdatedParticipant = false;
    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === layerUuid) {
          if (hasUpdatedLayer) {
            handleError({
              source: 'updateExcessTowerLayerParticipantCoverage',
              message: `Found multiple layers with uuid ${layerUuid}`,
            });
          }
          hasUpdatedLayer = true;

          return {
            ...layer,
            participants: sortExcessTowerLayerParticipants(
              layer.participants.map(
                (participant): IUnderwritingQuoteExcessLayerParticipant => {
                  if (participant.uuid === participantUuid) {
                    if (hasUpdatedParticipant) {
                      handleError({
                        source: 'updateExcessTowerLayerParticipantCoverage',
                        message: `Found multiple participants with uuid ${participantUuid}`,
                      });
                    }
                    hasUpdatedParticipant = true;
                    return {
                      ...participant,
                      coverages: participant.coverages.map(
                        (coverage): RaterParticipantCoverageDetails => {
                          const isSecurityCoverage =
                            coverage.name === 'Cyber Crime';
                          return {
                            ...coverage,
                            hasChanged: true,
                            quote: {
                              ...coverage.quote,
                              limit: isSecurityCoverage
                                ? coverage.quote.limit
                                : limit,
                            },
                          };
                        }
                      ),
                    };
                  }
                  return participant;
                }
              )
            ),
          };
        }

        return layer;
      });
    this.markQuoteAsChanged('cascadeTowerLimitAndRetention');

    this.requestRatingUpdate('cascadeTowerLimitAndRetention');
  }

  cascadeFusionScoreModifier(FusionScoreModifier: number | null) {
    this.draftState.quote.coverages = this.draftState.quote.coverages.map(
      (coverage) => {
        return {
          ...coverage,
          hasChanged: true,
          quote: {
            ...coverage.quote,
            selectedFusionScoreModifier: FusionScoreModifier,
          },
        };
      }
    );

    this.markQuoteAsChanged('cascadeFusionScoreModifier');
    this.requestRatingUpdate('cascadeFusionScoreModifier');
  }

  setTab(tab: RaterTab) {
    this.logger.log('setTab', tab);

    this.draftState.transitioningAwayFromTab = this.draftState.tab;
    this.draftState.tab = tab;
  }

  finishTabTransition() {
    this.draftState.transitioningAwayFromTab = undefined;
  }

  clearRaterError() {
    this.logger.log('clearRaterError');
    this.draftState.raterError = undefined;
    this.draftState.raterErrorDetails = undefined;
  }

  onDisconnectFromSocket() {
    this.logger.log('onDisconnectFromSocket');
    this.draftState.isAuthenticated = false;
    this.draftState.authenticationStatusChangedAt = new Date();
  }

  receiveSocketMessage(data: UnderwritingCoverageReceivedMessage) {
    this.logger.log('receiveSocketMessage', data);

    const lastMessage = this.state.messagesSent.find(
      (message) => message.id === this.state.lastMessageId
    );

    this.draftState.isRequestingRatingUpdate = false;

    if ('authenticatedBy' in data) {
      this.draftState.isAuthenticated = true;
      this.draftState.authenticationStatusChangedAt = new Date();
      return;
    }

    convergeLogger[data.type === 'error' ? 'error' : 'info'](`# Rater message

${data.type === 'error' ? data.message ?? 'N/A' : data.type}

## Request

\`\`\`json
${JSON.stringify(lastMessage?.data, null, 2)}
\`\`\`

## Response

\`\`\`json
${JSON.stringify(data, null, 2)}
\`\`\`
`);

    const parseErrors = (errors: CoverageErrorMessageDetailsArray) => {
      return errors.map((err) => {
        if (typeof err.path[0] === 'string') {
          const match = err.path[0].match(/coverage\[(\d+)\]/);
          return {
            [err.path[1]]: true,
            idx: match ? parseInt(match[1], 10) : null,
            message: err.message,
          };
        } else return err;
      });
    };

    if (data.type === 'error') {
      convergeLogger.error(`Error received from socket: ${data.message}`);
      convergeLogger.error(data.details);
      convergeLogger.error(data);

      const isSubLimitError =
        Array.isArray(data.details) &&
        data.details.some((d) => d.path?.join('.').includes('subLimit'));

      const errorDetails =
        typeof data.details === 'string'
          ? data.details
          : Array.isArray(data.details)
          ? `${data.details[0].path.join('.')}: ${data.details[0].message}`
          : JSON.stringify(data.details);

      this.draftState.raterError = isSubLimitError
        ? 'A sub-coverage limit cannot be greater than the main coverage limit. Please adjust the amount in order to proceed.'
        : data.details
        ? `${data.message}: ${
            typeof data.details === 'string'
              ? data.details
              : Array.isArray(data.details)
              ? data.details.length === 1
                ? `${errorDetails}`
                : `${errorDetails} (There ${
                    data.details.length - 1 === 1 ? 'is' : 'are'
                  } ${data.details.length - 1} more error${
                    data.details.length - 1 === 1 ? '' : 's'
                  })`
              : JSON.stringify(data.details)
          }`
        : data.message;

      this.draftState.raterErrorDetails = Array.isArray(data.details)
        ? parseErrors(data.details)
        : undefined;

      return;
    }

    if (!data.results) {
      convergeLogger.warn(
        `Invalid data received from socket (no results)`,
        data
      );
      this.draftState.raterError = `Invalid data received from socket (no results)`;
      return;
    }

    try {
      receiveRaterMessageAndUpdateState(this.draftState, data);
      this.draftState.lastReceivedMessageAt = new Date();
      this.draftState.raterError = undefined;
      this.draftState.raterErrorDetails = undefined;
      this.draftState.isInvalidData = false;

      if (this.draftState.isRequestingUpsertQuoteAfterRating) {
        this.requestUpsertQuote('receiveSocketMessage');
      }
    } catch (cause) {
      this.draftState.raterError = `Unable to parse socket response: ${cause}`;
      convergeLogger.error(``);

      handleError({
        notify: true,
        source: 'receiveSocketMessage',
        message: `Unable to parse socket response: ${cause}`,
        context: {
          message: {
            request: JSON.stringify(lastMessage?.data ?? lastMessage?.message),
            response: JSON.stringify(data),
          },
        },
      });
    }
  }

  expandNotesForUuid(uuid: string) {
    this.logger.log('expandNotesForUuid', uuid);

    this.draftState.expandNotesForUuid = uuid;
  }

  collapseNotes() {
    this.logger.log('collapseNotes');

    this.draftState.expandNotesForUuid = undefined;
  }

  sendSocketMessage(data: unknown) {
    this.logger.log('sendSocketMessage', data);

    this.draftState.lastMessageId++;
    this.draftState.messagesToSend.push({
      message: JSON.stringify(data),
      id: this.draftState.lastMessageId,
      data,
    });
  }

  markMessageSent(id: number) {
    this.logger.log('markMessageSent', id);

    const messageSent = this.draftState.messagesToSend.find(
      (message) => message.id === id
    );

    if (!messageSent) {
      const sentMessage = this.draftState.messagesSent.find(
        (message) => message.id === id
      );
      if (!sentMessage) {
        handleError({
          notify: false,
          source: 'markMessageSent',
          message: `Could not find message with id ${id} in: ${this.draftState.messagesToSend
            .map((message) => message.id)
            .join(', ')} or ${this.draftState.messagesSent
            .map((message) => message.id)
            .join(', ')}`,
          context: {
            message: {
              messagesToSend: this.draftState.messagesToSend,
              messagesSent: this.draftState.messagesSent,
            },
          },
        });
      }

      return;
    }

    this.draftState.messagesSent.push(messageSent);
    this.draftState.messagesToSend = this.draftState.messagesToSend.filter(
      (message) => message.id !== id
    );
  }

  setOptions(options: IRaterProps) {
    this.logger.log('setOptions', options);

    this.draftState.options = options;
  }

  updateQuote(data: IUnderwritingQuote) {
    this.logger.log('updateQuote', data);

    const { endorsements, subjectivities, ...newQuoteData } = data;

    this.draftState.quote = {
      ...newQuoteData,
      endorsements: endorsements.map(({ quote, ...endorsement }) => {
        return {
          ...endorsement,
          quote: {
            ...quote,
          },
        };
      }),
      subjectivities: subjectivities.map(({ quote, ...subjectivity }) => {
        return {
          ...subjectivity,
          quote: {
            ...quote,
          },
        };
      }),
    };
  }

  updateCalculatedQuoteData(source = 'unknown') {
    this.logger.log('updateCalculatedQuoteData', source);

    const newLimit = getAggregateLimitFromQuote(this.draftState.quote);
    const newRetention = getRetentionFromQuote(this.draftState.quote);

    if (
      newLimit !== this.draftState.quote.limit ||
      newRetention !== this.draftState.quote.retention
    ) {
      this.markQuoteAsChanged(`updateCalculatedQuoteData:limit-or-retention`);
    }

    this.draftState.quote.limit = newLimit;

    this.draftState.quote.retention = newRetention;

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map(
        (layer): IUnderwritingQuoteExcessTowerLayer => {
          const newLayerLimit = layer.primary
            ? this.draftState.quote.limit
            : getLayerLimitFromParticipants(layer.participants);

          const hasLayerLimitChanged = newLayerLimit !== layer.limit;

          if (hasLayerLimitChanged) {
            this.markQuoteAsChanged(
              `updateCalculatedQuoteData:layer-${layer.position}-limit`
            );
          }

          return {
            ...layer,
            hasChanged: layer.hasChanged || hasLayerLimitChanged,
            limit: newLayerLimit,
            attachmentPoint: layer.attachmentPoint,
            participants: layer.participants.map(
              (participant): IUnderwritingQuoteExcessLayerParticipant => {
                const newParticipantLimit =
                  layer.primary || layer.position === 0
                    ? this.draftState.quote.limit
                    : participant.quote?.limit;

                const hasParticipantLimitChanged =
                  participant.hasChanged ||
                  newParticipantLimit !== participant.quote?.limit;
                return {
                  ...participant,
                  hasChanged: hasParticipantLimitChanged,
                  quote: {
                    ...participant.quote,
                    uuid: participant.quote?.uuid ?? '',
                    limit: newParticipantLimit,
                    attachmentPoint: participant.quote?.attachmentPoint,
                  },
                };
              }
            ),
          };
        }
      );

    if (
      this.draftState.quote.excessTowerLayers.length > 0 &&
      this.draftState.quote.excessTowerLayers[0].position !== 0
    ) {
      handleError({
        source: 'updateCalculatedQuoteData',
        message: `The first excess layer does not have position 0 (it has position ${this.draftState.quote.excessTowerLayers[0].position})`,
        context: { layer: this.draftState.quote.excessTowerLayers[0] },
      });
    }

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.sort(
        (a, b) => (a.position ?? 99999) - (b.position ?? 99999)
      );

    for (const layer of this.draftState.quote.excessTowerLayers) {
      if (layer.position === 0 && !layer.primary) {
        handleError({
          source: 'updateCalculatedQuoteData',
          message: `Primary layer (${
            layer.name ?? 'unknown name'
          }) primary field is not true. Autofixing...`,
          notify: config.isNotProduction,
          context: {
            layer,
          },
        });
        layer.primary = true;
      }

      if (layer.position !== 0 && !!layer.primary) {
        handleError({
          source: 'updateCalculatedQuoteData',
          message: `Excess layer (${
            layer.name ?? 'unknown name'
          }) primary field is true. Autofixing...`,
          notify: config.isNotProduction,
          context: {
            layer,
          },
        });
        layer.primary = false;
      }
    }

    const minCorrectiveFactor = 0.75;
    const maxCorrectiveFactor = 1.25;

    const primaryRatedPremium = this.draftState.quote.ratedPremium ?? 0;

    const primaryActualPremium =
      this.draftState.quote.excessTowerLayers.find(
        (layer) => layer.position === 0 || !!layer.primary
      )?.boundPremium ?? 0;

    const unlimitedCorrectiveFactor =
      primaryRatedPremium / primaryActualPremium;

    const correctiveFactor = Math.max(
      Math.min(unlimitedCorrectiveFactor, maxCorrectiveFactor),
      minCorrectiveFactor
    );

    this.draftState.quote.excessSelectedCorrectiveFactorNew = correctiveFactor;

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer, index) => {
        const layerLimit =
          layer.participants.reduce(
            (acc, curr) => acc + (curr.quote?.limit ?? 0),
            0
          ) ||
          layer.limit ||
          null;

        const companyShare =
          layer.participants.reduce(
            (acc, curr) => acc + (curr.quote?.limit ?? 0),
            0
          ) / (layerLimit ?? 1);

        if (companyShare > 1) {
          throw new Error(
            `Company share is greater than 1 for layer ${index} (${layer.name}): ${companyShare}`
          );
        }
        const isPrimaryLayer = layer.primary;

        const newLayer: IUnderwritingQuoteExcessTowerLayer = {
          ...layer,
          companyShare,
          position: index,
          name: index === 0 ? 'Primary Layer' : `Excess Layer ${index}`,
          limit: layerLimit,
          attachmentPoint: layer.attachmentPoint,
          participants: layer.participants.map(
            (participant): IUnderwritingQuoteExcessLayerParticipant => {
              const limits = [
                ...participant.coverages.map(
                  (coverage) => coverage.quote.limit ?? 0
                ),
                ...participant.coverages.reduce<number[]>(
                  (acc, coverage): number[] => {
                    acc.push(
                      ...coverage.subCoverages.map(
                        (subCoverage) => subCoverage.quote.subLimit ?? 0
                      )
                    );

                    return acc;
                  },
                  []
                ),
                0,
              ];

              const highestLimitFromParticipantCoverages =
                limits.length === 0
                  ? this.draftState.quote.limit
                  : Math.max(...limits);

              const companyShare =
                (participant.quote?.limit ?? 0) / (layerLimit ?? 1);

              if (companyShare > 1) {
                throw new Error(
                  `Company share is greater than 1 for layer ${index} (${layer.name}) participant ${participant.name}: ${companyShare}`
                );
              }

              return {
                ...participant,
                quote: {
                  ...participant.quote,
                  uuid: participant.quote?.uuid || '',
                  boundPremium: isPrimaryLayer
                    ? layer.boundPremium
                    : participant.quote?.boundPremium,
                  limit: isPrimaryLayer
                    ? layerLimit
                    : typeof highestLimitFromParticipantCoverages === 'number'
                    ? participant.quote?.limit
                    : null,
                  attachmentPoint: participant.quote?.attachmentPoint,
                  companyShare,
                },
                name: getDisplayNameFromEntity(participant) ?? 'N/A',
              };
            }
          ),
        };

        layer.limit =
          layer.participants.reduce(
            (acc, curr) => acc + (curr.quote?.limit ?? 0),
            0
          ) ||
          layer.limit ||
          null;

        layer.companyShare =
          layer.participants.reduce(
            (acc, curr) => acc + (curr.quote?.limit ?? 0),
            0
          ) / (layerLimit ?? 1);

        return newLayer;
      });
  }

  toggleExpandedView(value?: boolean) {
    this.logger.log('toggleExpandedView', value);

    this.draftState.showExpandedView =
      value ?? !this.draftState.showExpandedView;
    this.draftState.columnCount = this.draftState.showExpandedView
      ? RaterColumnCount.Expanded
      : RaterColumnCount.Contracted;
  }

  updateCoverage({
    update,
    ...updatedCoverage
  }: IUnderwritingCoverage & { update: boolean }) {
    this.logger.log('updateCoverage', updatedCoverage);

    let hasUpdatedCoverage = false;

    this.draftState.quote.coverages = this.draftState.quote.coverages.map(
      (coverage): IUnderwritingCoverage => {
        if (coverage.uuid === updatedCoverage.uuid) {
          hasUpdatedCoverage = true;
          return {
            hasChanged: true,
            ...coverage,
            ...updatedCoverage,
          };
        }

        return coverage;
      }
    );

    this.draftState.quote.endorsements = this.draftState.quote.endorsements.map(
      (endorsement) => {
        const updatedQuote = {
          ...endorsement.quote,
          included: updatedCoverage.quote.included,
        };

        if (
          updatedCoverage.name === 'Cyber Crime' &&
          endorsement.uuid ===
            EndorsementUuid.CyberCrimeDefinitionOfLossAmendatory
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Bricked Equipment Replacement' &&
          endorsement.uuid ===
            EndorsementUuid.OperationalTechnologyBrickedEquipment
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Digital Asset Recovery' &&
          endorsement.uuid ===
            EndorsementUuid.OperationalTechnologyDigitalAssets
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        // we dont have this endorsement yet
        // if (
        //   (updatedCoverage.name === 'Bricked Equipment Replacement' ||
        //     updatedCoverage.name === 'Digital Asset Recovery') &&
        //   endorsement.uuid === EndorsementUuid.OperationalTechnologyBrickedEquipment
        // ) {
        //   return {
        //     ...endorsement,
        //     quote: {
        //       ...endorsement.quote,
        //       included: updatedCoverage.quote.included,
        //     },
        //   };
        // }
        if (
          updatedCoverage.name === 'Contingent Business Interruption' &&
          endorsement.uuid ===
            EndorsementUuid.ContingentBusinessInterruptionNonItProviders
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Contingent Business Interruption' &&
          endorsement.uuid ===
            EndorsementUuid.ContingentBusinessInterruptionNonItFranchisedRisk
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          (updatedCoverage.name === 'Regulatory Proceedings' ||
            updatedCoverage.name === 'Network Security & Privacy Liability' ||
            updatedCoverage.name === 'PCI-DSS Events') &&
          endorsement.uuid === EndorsementUuid.RetroactiveDateAmendatory
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Network Security & Privacy Liability' &&
          endorsement.uuid ===
            EndorsementUuid.ContingentBodilyInjuryAndPropertyDamageEndorsement
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Regulatory Proceedings' &&
          endorsement.uuid ===
            EndorsementUuid.WrongfulCollectionExclusionEndorsement
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }
        if (
          updatedCoverage.name === 'Incident Response' &&
          endorsement.uuid ===
            EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit
        ) {
          return {
            ...endorsement,
            quote: updatedQuote,
          };
        }

        // Remove endorsement if coverage parent not included
        if (
          endorsement.coverageUuid === updatedCoverage.uuid &&
          !updatedCoverage.quote.included
        ) {
          return {
            ...endorsement,
            quote: {
              ...endorsement.quote,
              included: updatedCoverage.quote.included,
            },
          };
        }

        return endorsement;
      }
    );

    if (!hasUpdatedCoverage) {
      handleError({
        source: 'RaterReducer.updateCoverage',
        message: `Could not find coverage with uuid ${updatedCoverage.uuid}`,
      });
    }

    // Cascade Proof of Loss changes to all other Proof of Loss subcoverages
    const isProofOfLossSubCoverage =
      updatedCoverage.uuid === SubCoverageUuid.ProofOfLoss;
    const hasProofOfLossSubCoverage = updatedCoverage.subCoverages?.some(
      (sub) => sub.uuid === SubCoverageUuid.ProofOfLoss && sub.hasChanged
    );

    if (
      (isProofOfLossSubCoverage || hasProofOfLossSubCoverage) &&
      hasUpdatedCoverage
    ) {
      const proofOfLossSubCoverage = isProofOfLossSubCoverage
        ? updatedCoverage
        : updatedCoverage.subCoverages?.find(
            (sub) => sub.uuid === SubCoverageUuid.ProofOfLoss && sub.hasChanged
          );

      if (proofOfLossSubCoverage) {
        this.cascadeProofOfLossChanges(proofOfLossSubCoverage);
      }
    }

    if (update) {
      this.requestRatingUpdate('updateCoverage');
    } else {
      this.updateCalculatedQuoteData('updateCoverage');
    }
  }

  updateSubjectivity({
    update,
    ...updatedSubjectivity
  }: IUnderwritingSubjectivity & { update: boolean }) {
    this.logger.log('updateSubjectivity', updatedSubjectivity);
    const { name } = updatedSubjectivity;
    if (this.draftState.quote.subjectivities) {
      this.draftState.quote.subjectivities =
        this.draftState.quote.subjectivities.map(
          (subjectivity): IUnderwritingSubjectivity => {
            if (subjectivity.uuid === updatedSubjectivity.uuid) {
              return {
                ...subjectivity,
                ...updatedSubjectivity,
                name: name,
                hasChanged: true,
              };
            }
            return subjectivity;
          }
        );
    }

    if (update) {
      this.requestRatingUpdate('updateSubjectivity');
    }
  }

  addSubjectivity(newSubjectivity: IUnderwritingSubjectivity) {
    this.logger.log('addSubjectivity', newSubjectivity);

    this.draftState.quote.subjectivities = [
      ...this.draftState.quote.subjectivities,
      {
        ...newSubjectivity,
        hasChanged: true,
        isNew: true,
      },
    ];

    this.requestRatingUpdate('addSubjectivity');
  }

  updateEndorsement({
    update,
    ...updatedEndorsement
  }: IUnderwritingEndorsement & { update: boolean }) {
    this.logger.log('updatedEndorsement', updatedEndorsement);
    const endorsementMappings: Record<string, { coverageNames: string[] }> = {
      [EndorsementUuid.CyberCrimeDefinitionOfLossAmendatory]: {
        coverageNames: ['Cyber Crime'],
      },
      [EndorsementUuid.OperationalTechnologyBrickedEquipment]: {
        coverageNames: ['Bricked Equipment Replacement'],
      },
      [EndorsementUuid.OperationalTechnologyDigitalAssets]: {
        coverageNames: ['Digital Asset Recovery'],
      },
      [EndorsementUuid.RetroactiveDateAmendatory]: {
        coverageNames: [
          'Regulatory Proceedings',
          'Network Security & Privacy Liability',
          'PCI-DSS Events',
        ],
      },
      [EndorsementUuid.ContingentBusinessInterruptionNonItProviders]: {
        coverageNames: ['Contingent Business Interruption'],
      },
      [EndorsementUuid.ContingentBusinessInterruptionNonItFranchisedRisk]: {
        coverageNames: ['Contingent Business Interruption'],
      },
      [EndorsementUuid.ContingentBodilyInjuryAndPropertyDamageEndorsement]: {
        coverageNames: ['Network Security & Privacy Liability'],
      },
      [EndorsementUuid.WrongfulCollectionExclusionEndorsement]: {
        coverageNames: ['Regulatory Proceedings'],
      },
      [EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit]: {
        coverageNames: ['Incident Response'],
      },
      // You can add more mappings as needed
    };

    if (this.draftState.quote.endorsements) {
      this.draftState.quote.endorsements =
        this.draftState.quote.endorsements.map(
          (endorsement): IUnderwritingEndorsement => {
            if (endorsement.uuid === updatedEndorsement.uuid) {
              const relevantCoverage = this.draftState.quote.coverages.find(
                (coverage) => coverage.quote.included === false
              );

              const mapping =
                updatedEndorsement.uuid &&
                endorsementMappings[updatedEndorsement.uuid];

              if (
                mapping &&
                mapping.coverageNames.includes(relevantCoverage?.name || '')
              ) {
                handleError({
                  notify: true,
                  source: '',
                  message: `Please ensure ${relevantCoverage?.name} coverage is included`,
                });

                return endorsement;
              }

              return {
                ...endorsement,
                ...updatedEndorsement,
                hasChanged: true,
              };
            }

            return endorsement;
          }
        );
    }

    // Example usage of the mappings in your function

    if (update) {
      this.requestRatingUpdate('updateEndorsement');
    } else {
      this.updateCalculatedQuoteData('updateEndorsement');
    }
  }

  addEndorsement(newEndorsement: IUnderwritingEndorsement) {
    this.logger.log('addEndorsement', newEndorsement);

    const updateIncluded = this.draftState.quote.coverages.some((coverage) => {
      switch (newEndorsement.uuid) {
        case EndorsementUuid.CyberCrimeDefinitionOfLossAmendatory:
          return coverage.name === 'Cyber Crime' && !coverage.quote.included;
        case EndorsementUuid.RetroactiveDateAmendatory:
          return (
            (coverage.name === 'Regulatory Proceedings' ||
              coverage.name === 'Network Security & Privacy Liability' ||
              coverage.name === 'PCI-DSS Events') &&
            !coverage.quote.included
          );
        case EndorsementUuid.WrongfulCollectionExclusionEndorsement:
          return (
            coverage.name === 'Regulatory Proceedings' &&
            !coverage.quote.included
          );
        case EndorsementUuid.OperationalTechnologyBrickedEquipment:
          return (
            coverage.name === 'Bricked Equipment Replacement' &&
            !coverage.quote.included
          );
        case EndorsementUuid.OperationalTechnologyDigitalAssets:
          return (
            coverage.name === 'Digital Asset Recovery' &&
            !coverage.quote.included
          );
        case EndorsementUuid.ContingentBusinessInterruptionNonItProviders:
          return (
            coverage.name === 'Contingent Business Interruption' &&
            !coverage.quote.included
          );
        case EndorsementUuid.ContingentBusinessInterruptionNonItFranchisedRisk:
          return (
            coverage.name === 'Contingent Business Interruption' &&
            !coverage.quote.included
          );
        case EndorsementUuid.ContingentBodilyInjuryAndPropertyDamageEndorsement:
          return (
            coverage.name === 'Network Security & Privacy Liability' &&
            !coverage.quote.included
          );
        case EndorsementUuid.AdditionalPrivacyEventExpensesOutSideLimit:
          return (
            coverage.name === 'Incident Response' && !coverage.quote.included
          );
        default:
          return false;
      }
    });
    const treatAsPremiumBearing =
      !newEndorsement.nonPremiumBearing ||
      TEMP_ENDORSEMENT_SIMILAR_PREMIUM_BEARING.includes(
        newEndorsement.uuid || ''
      );

    const newEndorsementData: IUnderwritingEndorsement = {
      ...newEndorsement,
      hasChanged: true,
      isNew: true,
      quote: {
        ...newEndorsement.quote,
        limit: treatAsPremiumBearing ? newEndorsement.quote.limit : 0,
        retention: treatAsPremiumBearing ? newEndorsement.quote.retention : 0,
        fusionScore:
          newEndorsement.quote.fusionScore ?? this.draftState.quote.fusionScore,
        included: !updateIncluded,
      },
    };

    this.draftState.quote.endorsements?.push(newEndorsementData);

    // When adding either tracking tech endorsement, deselect the counterpart
    // before the rating request fires so only one correct state is sent.
    const trackingCounterpartUuid =
      newEndorsement.uuid ===
      EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
        ? EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit
        : newEndorsement.uuid ===
          EndorsementUuid.TrackingTechnologyClaimsExpenseSublimit
        ? EndorsementUuid.TrackingTechnologyLiabilityCoverageEndorsement
        : null;

    if (trackingCounterpartUuid) {
      this.draftState.quote.endorsements =
        this.draftState.quote.endorsements.map((e) =>
          e.uuid === trackingCounterpartUuid && e.quote?.included
            ? { ...e, hasChanged: true, quote: { ...e.quote, included: false } }
            : e
        );
    }

    if (newEndorsement.questions.edges.length > 0) {
      this.requestRatingUpdateAndUpsertQuote(
        `addEndorsement:${newEndorsement.questions.edges.length}-${pluralize(
          'question',
          newEndorsement.questions.edges.length
        )}`
      );
    } else {
      if (newEndorsement.coverageUuid) {
        this.requestRatingUpdate('updateCoverage');
        return;
      }
      this.requestRatingUpdate('addEndorsement');
    }
  }

  addConvergeToFirstExcessLayer() {
    this.logger.log('addConvergeToFirstExcessLayer');

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    if (!this.draftState.quote.excessTowerLayers[1]) {
      this.addExcessTowerLayer();
    }

    const firstExcessLayer = this.draftState.quote.excessTowerLayers[1];

    if (!firstExcessLayer) {
      handleError({
        notify: true,
        source: 'addConvergeToFirstExcessLayer',
        message: `Could not find first excess layer`,
      });
      return;
    }

    if (firstExcessLayer.participants.find((p) => isConverge(p))) {
      handleError({
        notify: true,
        source: 'addConvergeToFirstExcessLayer',
        message: `Converge is already in the first excess layer`,
      });
      return;
    }

    this.addExcessTowerLayerParticipant(firstExcessLayer.uuid, {
      isNew: true,
      hasChanged: true,
      name: 'Converge Inc.',
      uuid: CONVERGE_UUID,
      aliases: {
        edges: [
          {
            cursor: 0,
            queryCursor: '0',
            node: {
              primary: true,
              name: 'Converge Inc.',
              sources: [Provider.Converge, Provider.Primary],
              uuid: `${CONVERGE_UUID}-alias`,
            },
          },
        ],
      },
      quote: {
        limit:
          firstExcessLayer.participants[0]?.quote?.limit ??
          this.draftState.quote.limit,
        uuid: uuidv4(),
      },
      coverages: [],
    });
  }

  updatePremium(premium: number) {
    this.draftState.quote.selectedQuotedPremium =
      premium !== 0 ? premium : null;

    this.requestRatingUpdate('premium');
  }

  addExcessTowerLayer() {
    this.logger.log('addExcessTowerLayer');

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    this.draftState.quote.excessTowerLayers.push({
      isNew: true,
      hasChanged: true,
      uuid: uuidv4(),
      limit:
        this.draftState.quote.limit ||
        getAggregateLimitFromQuote(this.draftState.quote),
      name: `Excess Layer ${this.draftState.quote.excessTowerLayers.length}`,
      participants: [],
      boundPremium: this.draftState.quote.ratedPremium ?? 1,
      position: this.draftState.quote.excessTowerLayers.length + 1,
      companyShare: 0,
    });

    this.requestRatingUpdate('addExcessTowerLayer');
  }

  addExcessTowerLayerParticipant(
    layerUuid: string,
    participant: IUnderwritingQuoteExcessLayerParticipant
  ) {
    this.logger.log('addExcessTowerLayerParticipant', layerUuid, participant);

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    let foundLayer = false;

    // Seed a new participant from the primary coverages (fresh quote uuids) so
    // its coverages show before save, mirroring the backend upsert.
    const seededCoverages =
      participant.coverages && participant.coverages.length > 0
        ? participant.coverages
        : (this.draftState.quote.coverages ?? []).map(
            (coverage): RaterParticipantCoverageDetails => ({
              ...(coverage as unknown as RaterParticipantCoverageDetails),
              hasChanged: true,
              quote: {
                ...coverage.quote,
                uuid: uuidv4(),
              } as RaterParticipantCoverageDetails['quote'],
              subCoverages: (coverage.subCoverages ?? []).map(
                (subCoverage): RaterParticipantSubCoverageDetails => ({
                  ...(subCoverage as unknown as RaterParticipantSubCoverageDetails),
                  hasChanged: true,
                  quote: {
                    ...subCoverage.quote,
                    uuid: uuidv4(),
                  } as RaterParticipantSubCoverageDetails['quote'],
                })
              ),
            })
          );

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map(
        (layer): IUnderwritingQuoteExcessTowerLayer => {
          if (layer.uuid === layerUuid) {
            foundLayer = true;

            return {
              ...layer,
              participants: sortExcessTowerLayerParticipants([
                ...layer.participants,
                {
                  ...participant,
                  isNew: true,
                  hasChanged: true,
                  name: getDisplayNameFromEntity(participant) ?? 'N/A',
                  coverages: seededCoverages,
                  quote: {
                    ...participant.quote,
                    uuid: participant.quote?.uuid ?? uuidv4(),
                    limit: 0,
                  },
                },
              ]),
            };
          }

          return layer;
        }
      );

    if (!foundLayer) {
      handleError({
        message: `Could not find layer for UUID ${layerUuid}`,
        source: 'addExcessTowerLayerParticipant',
        context: {
          message: {
            layerUuid,
            participant,
            quote: this.draftState.quote,
            layers: this.draftState.quote.excessTowerLayers,
          },
        },
      });
      return;
    }
  }

  replaceExcessTowerLayerParticipant(
    excessLayerUuid: string,
    participantUuid: string,
    newParticipant: IUnderwritingQuoteExcessLayerParticipant
  ) {
    this.logger.log(
      'replaceExcessTowerLayerParticipant',
      excessLayerUuid,
      participantUuid
    );

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    const layer =
      this.draftState.quote.excessTowerLayers.find(
        (layer) => layer.uuid === excessLayerUuid
      ) ?? this.draftState.quote.excessTowerLayers[0];

    if (!layer) {
      handleError({
        notify: true,
        source: 'replaceExcessTowerLayerParticipant',
        message: `Could not find layer ${excessLayerUuid}`,
      });
      return;
    }

    let hasUpdatedParticipant = false;

    layer.participants = sortExcessTowerLayerParticipants(
      layer.participants.map(
        (participant): IUnderwritingQuoteExcessLayerParticipant => {
          if (participant.uuid === participantUuid) {
            hasUpdatedParticipant = true;
            return {
              ...participant,
              ...newParticipant,
              hasChanged: true,
              name: getDisplayNameFromEntity(newParticipant) ?? 'N/A',
            };
          }

          return participant;
        }
      )
    );

    if (!hasUpdatedParticipant) {
      handleError({
        notify: true,
        source: 'replaceExcessTowerLayerParticipant',
        message: `Could not find participant ${participantUuid} in layer ${excessLayerUuid}`,
      });
      return;
    }

    this.requestRatingUpdateAndUpsertQuote(
      'replaceExcessTowerLayerParticipant'
    );
  }

  expandCoverageListForExcessTowerLayerParticipant(
    excessLayerUuid: string,
    participantUuid: string
  ) {
    this.logger.log(
      'expandCoverageListForExcessTowerLayerParticipant',
      excessLayerUuid,
      participantUuid
    );

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    this.draftState.expandCoverageListForExcessTowerLayerParticipants.push([
      excessLayerUuid,
      participantUuid,
    ]);
  }

  contractCoverageListForExcessTowerLayerParticipant(
    excessLayerUuid: string,
    participantUuid: string
  ) {
    this.logger.log(
      'contractCoverageListForExcessTowerLayerParticipant',
      excessLayerUuid,
      participantUuid
    );

    this.draftState.expandCoverageListForExcessTowerLayerParticipants =
      this.draftState.expandCoverageListForExcessTowerLayerParticipants.filter(
        ([layer, participant]) => {
          return layer !== excessLayerUuid && participant !== participantUuid;
        }
      );
  }

  removeParticipantFromLayer(excessLayerUuid: string, participantUuid: string) {
    this.logger.log(
      'removeParticipantFromLayer',
      excessLayerUuid,
      participantUuid
    );

    this.draftState.expandCoverageListForExcessTowerLayerParticipants = [];

    const layer =
      this.draftState.quote.excessTowerLayers.find(
        (layer) => layer.uuid === excessLayerUuid
      ) ?? this.draftState.quote.excessTowerLayers[0];

    if (!layer) {
      this.logger.error(`Could not find layer ${excessLayerUuid}`);
      return;
    }

    layer.participants = sortExcessTowerLayerParticipants(
      layer.participants.filter(
        (participant) => participant.uuid !== participantUuid
      )
    );

    this.requestRatingUpdate('removeParticipantFromLayer');
  }

  updateExcessTowerLayer(
    excessLayerUuid: string,
    {
      limit,
      boundPremium,
      attachmentPoint,
      cascadeToParticipants = false,
    }: {
      limit?: number | null;
      boundPremium?: number | null;
      attachmentPoint?: number | null;
      cascadeToParticipants?: boolean;
    }
  ) {
    this.logger.log(
      'updateExcessTowerLayer',
      excessLayerUuid,
      limit,
      boundPremium
    );

    let hasFoundLayer = false;

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === excessLayerUuid) {
          hasFoundLayer = true;

          if (isNotNullOrUndefined(limit)) {
            layer.hasChanged = layer.hasChanged || limit !== layer.limit;
            layer.limit = limit;
          }

          if (isNotNullOrUndefined(boundPremium)) {
            layer.hasChanged =
              layer.hasChanged || boundPremium !== layer.boundPremium;
            layer.boundPremium = boundPremium;
          }

          if (isNotNullOrUndefined(attachmentPoint)) {
            layer.hasChanged =
              layer.hasChanged || attachmentPoint !== layer.attachmentPoint;
            layer.attachmentPoint = attachmentPoint;
          }

          if (cascadeToParticipants) {
            layer.participants = sortExcessTowerLayerParticipants(
              layer.participants.map(
                (participant): IUnderwritingQuoteExcessLayerParticipant => {
                  const newLimit =
                    limit ?? participant.quote?.limit ?? layer.limit ?? 0;
                  return {
                    ...participant,
                    hasChanged:
                      participant.hasChanged ||
                      newLimit !== participant.quote?.limit,
                    quote: {
                      ...participant.quote,
                      uuid: participant.quote?.uuid || '',
                      limit: newLimit,
                    },
                  };
                }
              )
            );
          }
        }

        return layer;
      });

    if (!hasFoundLayer) {
      this.logger.error(
        `Could not find excess layer with uuid ${excessLayerUuid}`
      );
      return;
    }

    this.requestRatingUpdate('updateExcessTowerLayer');
  }

  updateExcessTowerLayerParticipantCoverage(
    excessLayerUuid: string,
    participantUuid: string,
    coverageUuid: string,
    args: {
      limit?: number | null;
      erosion?: boolean;
      included?: boolean;
      participantCoverageName?: string;
    }
  ) {
    const { limit, erosion, included, participantCoverageName } = args;
    this.logger.log('updateExcessTowerLayerParticipantCoverage', {
      excessLayerUuid,
      participantUuid,
      coverageUuid,
      limit,
      erosion,
      included,
      participantCoverageName,
    });

    let hasUpdatedLayer = false;
    let hasUpdatedParticipant = false;
    let hasUpdatedParticipantCoverage = false;

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === excessLayerUuid) {
          if (hasUpdatedLayer) {
            handleError({
              source: 'updateExcessTowerLayerParticipantCoverage',
              message: `Found multiple layers with uuid ${excessLayerUuid}`,
            });
          }
          hasUpdatedLayer = true;
          return {
            ...layer,
            participants: sortExcessTowerLayerParticipants(
              layer.participants.map(
                (participant): IUnderwritingQuoteExcessLayerParticipant => {
                  if (participant.uuid === participantUuid) {
                    if (hasUpdatedParticipant) {
                      handleError({
                        source: 'updateExcessTowerLayerParticipantCoverage',
                        message: `Found multiple participants with uuid ${participantUuid}`,
                      });
                    }
                    hasUpdatedParticipant = true;
                    return {
                      ...participant,
                      coverages: participant.coverages.map(
                        (coverage): RaterParticipantCoverageDetails => {
                          if (coverage.uuid === coverageUuid) {
                            if (hasUpdatedParticipantCoverage) {
                              handleError({
                                source:
                                  'updateExcessTowerLayerParticipantCoverage',
                                message: `Found multiple coverages with uuid ${coverageUuid}`,
                              });
                            }
                            hasUpdatedParticipantCoverage = true;
                            return {
                              ...coverage,
                              hasChanged:
                                coverage.hasChanged ||
                                limit !== coverage.quote.limit ||
                                erosion !== coverage.quote.erosion ||
                                erosion !== coverage.quote.included ||
                                participantCoverageName !==
                                  coverage.quote.participantCoverageName,
                              quote: {
                                ...coverage.quote,
                                limit: limit ?? coverage.quote.limit,
                                erosion: erosion ?? coverage.quote.erosion,
                                included: included ?? coverage.quote.included,
                                participantCoverageName:
                                  participantCoverageName ??
                                  coverage.quote.participantCoverageName,
                              },
                              subCoverages: coverage.subCoverages.map(
                                (
                                  subCoverage
                                ): RaterParticipantSubCoverageDetails => {
                                  const nowIncluded =
                                    'included' in args
                                      ? included
                                        ? included
                                        : false
                                      : subCoverage.quote.included;
                                  return {
                                    ...subCoverage,
                                    hasChanged:
                                      subCoverage.hasChanged ||
                                      subCoverage.quote.included !==
                                        nowIncluded,
                                    quote: {
                                      ...subCoverage.quote,
                                      included: nowIncluded,
                                    },
                                  };
                                }
                              ),
                            };
                          }
                          return coverage;
                        }
                      ),
                    };
                  }
                  return participant;
                }
              )
            ),
          };
        }

        return layer;
      });

    if (!hasUpdatedLayer) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipantCoverage',
        message: `Could not find layer ${excessLayerUuid}`,
      });
    } else if (!hasUpdatedParticipant) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipantCoverage',
        message: `Could not find participant ${participantUuid} in layer ${excessLayerUuid}`,
      });
    }

    this.requestRatingUpdate('updateExcessTowerLayerParticipantCoverage');
  }

  updateExcessTowerLayerParticipantSubCoverage(
    excessLayerUuid: string,
    participantUuid: string,
    coverageUuid: string,
    subCoverageUuid: string,
    {
      subLimit,
      erosion,
      included,
      participantCoverageName,
    }: {
      subLimit?: number | null;
      erosion?: boolean;
      included?: boolean;
      participantCoverageName?: string;
    }
  ) {
    this.logger.log('updateExcessTowerLayerParticipantSubCoverage', {
      excessLayerUuid,
      participantUuid,
      subCoverageUuid,
      subLimit,
      erosion,
      included,
      coverageUuid,
    });

    let hasUpdatedLayer = false;
    let hasUpdatedParticipant = false;

    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === excessLayerUuid) {
          hasUpdatedLayer = true;
          return {
            ...layer,
            participants: sortExcessTowerLayerParticipants(
              layer.participants.map(
                (participant): IUnderwritingQuoteExcessLayerParticipant => {
                  if (participant.uuid === participantUuid) {
                    hasUpdatedParticipant = true;
                    return {
                      ...participant,
                      coverages: participant.coverages.map((coverage) => {
                        if (coverage.uuid !== coverageUuid) {
                          return coverage;
                        }
                        return {
                          ...coverage,
                          subCoverages: coverage.subCoverages.map(
                            (subCoverage) => {
                              if (subCoverage.uuid === subCoverageUuid) {
                                return {
                                  ...subCoverage,
                                  hasChanged:
                                    subCoverage.hasChanged ||
                                    subLimit !== subCoverage.quote.subLimit ||
                                    erosion !== subCoverage.quote.erosion ||
                                    included !== subCoverage.quote.included ||
                                    participantCoverageName !==
                                      subCoverage.quote.participantCoverageName,
                                  quote: {
                                    ...subCoverage.quote,
                                    subLimit:
                                      subLimit ?? subCoverage.quote.subLimit,
                                    erosion:
                                      erosion ?? subCoverage.quote.erosion,
                                    included:
                                      included ?? subCoverage.quote.included,
                                    participantCoverageName:
                                      participantCoverageName ??
                                      subCoverage.quote.participantCoverageName,
                                  },
                                };
                              }

                              return subCoverage;
                            }
                          ),
                        };
                      }),
                    };
                  }
                  return participant;
                }
              )
            ),
          };
        }

        return layer;
      });

    if (!hasUpdatedLayer) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipantSubCoverage',
        message: `Could not find layer ${excessLayerUuid}`,
      });
    } else if (!hasUpdatedParticipant) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipantSubCoverage',
        message: `Could not find participant ${participantUuid} in layer ${excessLayerUuid}`,
      });
    }

    this.requestRatingUpdate('updateExcessTowerLayerParticipantSubCoverage');
  }

  updateExcessTowerLayerParticipant(
    excessLayerUuid: string,
    participantUuid: string,
    args: {
      limit?: number | null;
      policyReferenceId?: string;
      coverages?: RaterParticipantCoverageDetails[];
    }
  ) {
    const { limit, policyReferenceId, coverages } = args;

    this.logger.log(
      'updateExcessTowerLayerParticipant',
      excessLayerUuid,
      limit,
      policyReferenceId
    );

    let hasUpdatedLayer = false;
    let hasUpdatedParticipant = false;
    this.draftState.quote.excessTowerLayers =
      this.draftState.quote.excessTowerLayers.map((layer) => {
        if (layer.uuid === excessLayerUuid) {
          hasUpdatedLayer = true;
          return {
            ...layer,
            participants: layer.participants.map(
              (participant): IUnderwritingQuoteExcessLayerParticipant => {
                if (participant.uuid === participantUuid) {
                  hasUpdatedParticipant = true;
                  const newPolicyReferenceId =
                    policyReferenceId ??
                    participant.quote?.policyReferenceId ??
                    '';
                  const newLimit = limit ?? participant.quote?.limit;
                  return {
                    ...participant,
                    hasChanged:
                      participant.hasChanged ||
                      limit !== participant.quote?.limit ||
                      newPolicyReferenceId !==
                        participant.quote?.policyReferenceId,

                    quote: {
                      uuid: participant.quote?.uuid || '',
                      ...participant.quote,
                      limit: newLimit,
                      policyReferenceId: newPolicyReferenceId,
                    },
                    coverages: [...(coverages ?? participant.coverages)],
                  };
                }
                return participant;
              }
            ),
          };
        }

        return layer;
      });

    if (!hasUpdatedLayer) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipant',
        message: `Could not find layer ${excessLayerUuid}`,
      });
    } else if (!hasUpdatedParticipant) {
      handleError({
        notify: true,
        source: 'updateExcessTowerLayerParticipant',
        message: `Could not find participant ${participantUuid} in layer ${excessLayerUuid}`,
      });
    }

    if ('limit' in args || 'policyReferenceId' in args || 'coverages' in args) {
      this.requestRatingUpdate('updateExcessTowerLayerParticipant');
    } else {
      this.updateCalculatedQuoteData('updateExcessTowerLayerParticipant');
    }
  }
  // updateExcessTowerLayerParticipant(
  //   excessLayerUuid: string,
  //   participantUuid: string
  // ) {
  //   this.logger.log('updateExcessTowerLayerParticipant');

  //   const excessLayer =
  //     this.draftState.quote.excessTowerLayers.excessLayers.find(
  //       (layer) => layer.uuid === excessLayerUuid
  //     );

  //   if (!excessLayer) {
  //     this.logger.error(
  //       `Could not find excess layer with uuid ${excessLayerUuid}`
  //     );
  //     return;
  //   }

  //   const participant = excessLayer.participants.find(
  //     (participant) => participant.uuid === participantUuid
  //   );

  //   if (!participant) {
  //     this.logger.error(
  //       `Could not find participant with uuid ${participantUuid}`
  //     );
  //     return;
  //   }

  //   participant.limit = 0;
  // }

  setEndorsementsRetroactiveDate(
    uuid: string | null | undefined,
    retroactiveDate: Date | null | undefined,
    fullPriorActs: boolean | null | undefined,
    defaultDate?: boolean
  ) {
    this.logger.log('setEndorsementsRetroactiveDate', {
      uuid,
      retroactiveDate,
      fullPriorActs,
    });

    if (this.draftState.quote.endorsements) {
      this.draftState.quote.endorsements =
        this.draftState.quote.endorsements.map((endorsement) => {
          if (endorsement.uuid === uuid) {
            convergeLogger.info(
              `Setting retroactive date for endorsement ${endorsement.name} (${
                endorsement.uuid
              }) to ${retroactiveDate?.toISOString()}`
            );

            if (fullPriorActs && retroactiveDate) {
              convergeLogger.warn(
                `Full Prior Acts and Retroactive Date are both set for endorsement ${endorsement.name} (${endorsement.uuid}). This is a violation of business rules.`
              );
            }
            let newRetroactiveDate: string | null | undefined = undefined;

            if (!fullPriorActs) {
              newRetroactiveDate = retroactiveDate
                ? moment
                    .utc(retroactiveDate)
                    .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
                : null;
            } else {
              newRetroactiveDate = null;
            }
            const newFullPriorActs = fullPriorActs ?? false;

            const retroactiveDateOnly = (
              date: string | null | undefined
            ): string | null | undefined => (date ? date.slice(0, 10) : date);

            return {
              ...endorsement,
              hasChanged:
                endorsement.hasChanged ||
                newFullPriorActs !== endorsement.quote.fullPriorActs ||
                retroactiveDateOnly(newRetroactiveDate) !==
                  retroactiveDateOnly(endorsement.quote.retroactiveDate),
              quote: {
                ...endorsement.quote,
                retroactiveDate: newRetroactiveDate,
                fullPriorActs: newFullPriorActs,
              },
            };
          }
          return endorsement;
        });
    }

    this.requestRatingUpdate('setEndorsementsRetroactiveDate');
  }

  setCoveragesRetroactiveDate(
    uuid: string | null | undefined,
    retroactiveDate: Date | null | undefined,
    fullPriorActs: boolean | null | undefined,
    defaultDate?: boolean
  ) {
    this.logger.log('setCoveragesRetroactiveDate', {
      uuid,
      retroactiveDate,
      fullPriorActs,
    });

    if (this.draftState.quote.coverages) {
      this.draftState.quote.coverages = this.draftState.quote.coverages.map(
        (coverage) => {
          if (coverage.uuid === uuid) {
            convergeLogger.info(
              `Setting retroactive date for coverage ${coverage.name} (${coverage.uuid}) to ${retroactiveDate}`
            );
            if (fullPriorActs && retroactiveDate) {
              convergeLogger.warn(
                `Full Prior Acts and Retroactive Date are both set for coverage ${coverage.name} (${coverage.uuid}). This is a violation of business rules.`
              );
            }

            let newRetroactiveDate: string | null | undefined = undefined;

            if (!fullPriorActs) {
              newRetroactiveDate =
                moment.utc(retroactiveDate).format('YYYY-MM-DDTHH:mm:ss.SSS') +
                'Z';
            } else {
              newRetroactiveDate = null;
            }

            const newFullPriorActs = fullPriorActs ?? false;
            return {
              ...coverage,
              hasChanged:
                coverage.hasChanged ||
                newFullPriorActs !== coverage.quote.fullPriorActs ||
                newRetroactiveDate !== coverage.quote.retroactiveDate,
              quote: {
                ...coverage.quote,
                retroactiveDate: newRetroactiveDate,
                fullPriorActs: newFullPriorActs,
              },
            };
          }
          return coverage;
        }
      );
    }

    this.requestRatingUpdate('setCoveragesRetroactiveDate');
  }

  setEnterpriseRatings(data: IUnderwritingEnterpriseRating[]) {
    this.logger.log('setEnterpriseRatings', data);
    this.draftState.quote = {
      ...this.draftState.quote,
      enterpriseRatings: [...data],
    };

    this.requestRatingUpdate('setEnterpriseRatings');
  }

  setEndorsementImpact(endorsementImpact: SignificantTermsConditions) {
    this.logger.log('setEndorsementImpact', endorsementImpact);

    this.draftState.quote = {
      ...this.draftState.quote,
      hasEndorsementImpactChanged:
        endorsementImpact !== this.draftState.quote.endorsementImpact,
      endorsementImpact,
    };

    const newStcModifier = toSignificantTermsConditionsValue(endorsementImpact);

    if (
      this.draftState.quote.stcSelectedModifier &&
      newStcModifier !== this.draftState.quote.stcSelectedModifier
    ) {
      this.draftState.quote.hasStcSelectedModifierChanged = true;
      this.draftState.quote.stcSelectedModifier = newStcModifier;
    }

    this.draftState.quote.stcModifier = newStcModifier;

    this.requestRatingUpdate('setEndorsementImpact');
  }

  setExcessRatingEndorsementImpact(
    endorsementImpact: SignificantTermsConditions
  ) {
    this.logger.log('setExcessRatingEndorsementImpact', endorsementImpact);

    this.draftState.quote = {
      ...this.draftState.quote,
      hasExcessEndorsementImpactChanged:
        this.draftState.quote.excessEndorsementImpact !== endorsementImpact,
      excessEndorsementImpact: endorsementImpact,
    };

    const newStcModifier = toSignificantTermsConditionsValue(endorsementImpact);

    if (
      this.draftState.quote.stcExcessSelectedModifier &&
      newStcModifier !== this.draftState.quote.stcExcessSelectedModifier
    ) {
      this.draftState.quote.hasStcExcessSelectedModifierChanged = true;
      this.draftState.quote.stcExcessSelectedModifier = newStcModifier;
    }

    this.requestRatingUpdate('setExcessRatingEndorsementImpact');
  }

  setQuoteField<
    K extends keyof IUnderwritingQuote = keyof IUnderwritingQuote,
    V extends IUnderwritingQuote[K] = IUnderwritingQuote[K]
  >(field: K, value: V, requestRatingUpdate = true) {
    this.logger.log(
      'setQuoteField',
      'field:',
      field,
      'value:',
      value,
      'request rating update:',
      requestRatingUpdate ? 'yes' : 'no'
    );

    this.compareFieldAndRecordChange(field, value);

    switch (field) {
      case 'erSelectedFusionScoreModifier':
        this.draftState.quote.hasErSelectedFusionScoreModifierChanged =
          this.draftState.changedFields.includes(
            'erSelectedFusionScoreModifier'
          );
        break;
      case 'stcSelectedModifier':
        this.draftState.quote.hasStcSelectedModifierChanged =
          this.draftState.changedFields.includes('stcSelectedModifier');
        break;
      case 'stcExcessSelectedModifier':
        this.draftState.quote.hasStcExcessSelectedModifierChanged =
          this.draftState.changedFields.includes('stcExcessSelectedModifier');
        break;
      case 'excessSelectedCorrectiveFactor':
        this.draftState.quote.hasExcessSelectedCorrectiveFactorChanged =
          this.draftState.changedFields.includes(
            'excessSelectedCorrectiveFactor'
          );
        break;
    }

    this.draftState.quote[field] = value;

    this.markQuoteAsChanged(`setQuoteField:${field}`);

    if (requestRatingUpdate) {
      this.requestRatingUpdate(`setQuoteField:${field}`);
    }
  }

  setQuoteFields(
    fields: Partial<IUnderwritingQuote>,
    requestRatingUpdate = true
  ) {
    this.logger.log(
      'setQuoteFields',
      'fields:',
      fields,
      'request rating update:',
      requestRatingUpdate ? 'yes' : 'no'
    );

    for (const field in fields) {
      this.compareFieldAndRecordChange(
        field as keyof IUnderwritingQuote,
        fields[field as keyof IUnderwritingQuote]
      );
    }

    this.draftState.quote = {
      ...this.draftState.quote,
      ...fields,
    };

    this.markQuoteAsChanged('setQuoteFields');

    this.requestRatingUpdate('setQuoteFields');
  }

  compareFieldAndRecordChange<K extends keyof IUnderwritingQuote>(
    field: K,
    value: IUnderwritingQuote[K]
  ) {
    this.logger.log('compareFieldAndRecordChange', field, value);

    if (!field) {
      throw new Error(`Field is required. Value: ${value}`);
    }

    const hasFieldChanged =
      JSON.stringify(this.draftState.quote[field]) !== JSON.stringify(value);

    if (hasFieldChanged) {
      this.draftState.changedFields.push(field);
      this.draftState.changedFields = [
        ...new Set(this.draftState.changedFields),
      ];
    }
  }

  setQuoteName(value: string) {
    this.logger.log('setQuoteName', value);
    this.draftState.quote.name = value;

    this.markQuoteAsChanged('setQuoteName');
  }

  updateAfterQuoteUpsert() {
    this.draftState.hasSavedQuote = true;
    this.draftState.changedFields = [];
    this.draftState.lastEditedTab = undefined;
    // Mirror the backend invariant: TE&O endorsements default
    // retroactive_date to the policy start date with full_prior_acts=false.
    // Stored as a date-only string (YYYY-MM-DD) so the popover renders
    // it without timezone shifting from the policy_start_date datetime.
    const noPriorActsEndorsements: string[] = [
      EndorsementUuid.TechnologyErrorsAndOmissionsEndorsement,
      EndorsementUuid.TechnologyErrorsAndOmissionsEndorsementNA,
    ];
    const policyStartDateOnly = this.draftState.submission.policyStartDate
      ? String(this.draftState.submission.policyStartDate).slice(0, 10)
      : null;
    this.draftState.quote = {
      ...this.draftState.quote,
      hasChanged: false,
      coverages: this.draftState.quote.coverages.map(
        (coverage): IUnderwritingCoverage => ({
          ...coverage,
          hasChanged: false,
          quote: {
            ...coverage.quote,
          },
          subCoverages: coverage.subCoverages.map(
            (subCoverage): IUnderwritingSubCoverage => ({
              ...subCoverage,
              hasChanged: false,
              quote: {
                ...subCoverage.quote,
              },
            })
          ),
        })
      ),
      endorsements: this.draftState.quote.endorsements.map(
        (endorsement): IUnderwritingEndorsement => {
          const isNoPriorActs =
            !!endorsement.uuid &&
            noPriorActsEndorsements.includes(endorsement.uuid) &&
            !!policyStartDateOnly &&
            !endorsement.quote.fullPriorActs;
          return {
            ...endorsement,
            hasChanged: false,
            isNew: false,
            quote: {
              ...endorsement.quote,
              ...(isNoPriorActs && !endorsement.quote.retroactiveDate
                ? {
                    retroactiveDate: policyStartDateOnly,
                    fullPriorActs: false,
                  }
                : {}),
            },
          };
        }
      ),
      subjectivities: this.draftState.quote.subjectivities.map(
        (subjectivity): IUnderwritingSubjectivity => ({
          ...subjectivity,
          hasChanged: false,
          isNew: false,
          quote: {
            ...subjectivity.quote,
          },
        })
      ),
      excessTowerLayers: !this.draftState.quote.excessTowerLayers
        ? this.draftState.quote.excessTowerLayers
        : this.draftState.quote.excessTowerLayers.map(
            (layer): IUnderwritingQuoteExcessTowerLayer => ({
              ...layer,
              participants: sortExcessTowerLayerParticipants(
                layer.participants.map(
                  (participant): IUnderwritingQuoteExcessLayerParticipant => ({
                    ...participant,
                    hasChanged: false,
                    isNew: false,
                  })
                )
              ),
              hasChanged: false,
              isNew: false,
            })
          ),
    };
  }
}

export const RaterReducerActions = createActionCreators(RaterReducer);

export const RaterReducerFunction = createReducerFunction(RaterReducer);

export const useRaterReducer = (
  props: IRaterProps,
  quote: IUnderwritingQuote,
  submission: Submission,
  userQuotes: RaterUserQuotes,
  policy?: RaterPolicyDetails
) => {
  const initialState = createRaterData(
    props,
    quote,
    submission,
    userQuotes,
    policy
  );

  const [state, dispatch] = useReducer(RaterReducerFunction, initialState);

  return [state, dispatch, RaterReducerActions] as [
    typeof state,
    typeof dispatch,
    typeof RaterReducerActions
  ];
};

export type RaterReducerState = IRaterContextState;

export type RaterReducerDispatch = ReturnType<typeof useRaterReducer>[1];
type QuoteDataKey = 'limit' | 'retention';

const extractDataFromQuote = (
  quote: IUnderwritingQuote,
  dataKey: QuoteDataKey
): Record<string, number> => {
  return [...quote.endorsements, ...quote.coverages]
    .filter(
      (lineItem) =>
        lineItem.quote?.included === true &&
        typeof lineItem.quote[dataKey] === 'number'
    )
    .map((lineItem, index) => ({
      [lineItem.name ?? `line-item-${index}`]: lineItem.quote?.[dataKey] ?? 0,
    }))
    .reduce((acc, curr) => {
      return {
        ...acc,
        ...curr,
      };
    }, {});
};

// You can now use the above function to create the original functions

const getLimitsFromQuote = (
  quote: IUnderwritingQuote
): Record<string, number> => {
  return extractDataFromQuote(quote, 'limit');
};

const getRetentionsFromQuote = (
  quote: IUnderwritingQuote
): Record<string, number> => {
  return extractDataFromQuote(quote, 'retention');
};

type ExtractorFunction = (quote: IUnderwritingQuote) => Record<string, number>;

const getMaxValueFromQuote = (
  quote: IUnderwritingQuote,
  extractorFn: ExtractorFunction
) => {
  const values = extractorFn(quote);
  return Math.max(...Object.values(values));
};

export const getAggregateLimitFromQuote = (quote: IUnderwritingQuote) => {
  return getMaxValueFromQuote(quote, getLimitsFromQuote);
};

export const getRetentionFromQuote = (quote: IUnderwritingQuote) => {
  return getMaxValueFromQuote(quote, getRetentionsFromQuote);
};

const SIGNIFICANT_TERMS_CONDITIONS_VALUES = {
  [SignificantTermsConditions.VeryRestrictive]: 0.75,
  [SignificantTermsConditions.SomewhatRestrictive]: 0.85,
  [SignificantTermsConditions.Neutral]: 1,
  [SignificantTermsConditions.SomewhatExpansive]: 1.15,
  [SignificantTermsConditions.VeryExpansive]: 1.25,
};

export const toSignificantTermsConditionsValue = (
  key: SignificantTermsConditions
) => {
  return SIGNIFICANT_TERMS_CONDITIONS_VALUES[key];
};

const sortExcessTowerLayerParticipants = (
  participants: IUnderwritingQuoteExcessLayerParticipant[]
) => {
  return participants.sort((a, b) => {
    if (isConverge(b)) {
      return 1;
    }
    if (isConverge(a)) {
      return -1;
    }
    const aLimit = a.quote?.limit ?? 0;
    const bLimit = b.quote?.limit ?? 0;
    const diff = bLimit - aLimit;
    if (diff === 0) {
      return a.name.localeCompare(b.name);
    }
    return diff;
  });
};
